import Big from 'big.js';
import classNames from 'classnames';
import { ChangeEvent, ReactNode, useCallback } from 'react';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { TextInput } from 'ui';
import { RoundingType } from 'api';

interface CurrencyInputProps {
  name?: string;
  error?: string;
  label?: string | ReactNode;
  disabled?: boolean;
  placeholder?: string;
  value?: string | number;
  allowFractional?: boolean;
  onChange: (value: number | string) => void;
  decimalPoints?: number;
  roundingType?: RoundingType | undefined;
  className?: string;
  classNameCurrency?: string;
  currency?: string;
  withBorder?: boolean;
}

const CurrencyInput = ({
  name,
  label,
  error,
  disabled,
  value,
  placeholder,
  allowFractional = true,
  onChange,
  decimalPoints = 2,
  roundingType,
  className,
  classNameCurrency,
  currency = '$',
  withBorder,
}: CurrencyInputProps) => {
  const step = 1 / 10 ** decimalPoints;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value || '';

      if (!value) {
        onChange(value);
        return;
      }

      //We remove all the characters, except for numbers, commas and points
      value = value.replace(/[^\d.,]/g, '');

      //We allow only one point
      const parts = value.split('.');
      if (parts.length > 2) {
        value = parts[0] + '.' + parts.slice(1).join('');
      }

      //Comma processing
      const hasDot = value.includes('.');
      if (hasDot) {
        // If there is a point, the commas are just deleted
        value = value.replace(/,/g, '');
      } else {
        // if there is no point, but there is one comma, replace it with a point
        const commas = value.match(/,/g)?.length || 0;
        if (commas === 1) {
          value = value.replace(',', '.');
        } else {
          // several commas - we delete everything
          value = value.replace(/,/g, '');
        }
      }

      const regex = /^[0-9.,]*$/;

      if (!regex.test(value)) {
        return;
      }

      value = value.replaceAll(',', '.').replaceAll('..', '.');
      if (value === '.') {
        return;
      }
      if (hasDot) {
        const frac = value.split('.')?.[1] || '';
        if (value.endsWith('.') || frac.length <= decimalPoints) {
          onChange(value); // keep as string while user types
          return;
        }
      }

      let num = new Big(value);
      if (allowFractional) {
        num = num.round(decimalPoints, BigRoundMode.roundHalfUp);
      } else {
        num =
          roundingType === RoundingType.Up
            ? num.round(0, BigRoundMode.roundUp)
            : num.round(0, BigRoundMode.roundDown);
      }

      onChange(Number(num.valueOf()));
    },
    [allowFractional, decimalPoints, onChange, roundingType],
  );

  const paddingLeft = currency
    ? `calc(${currency.length * 0.8}rem + ${withBorder ? '4px' : '0px'})`
    : undefined;

  return (
    <div className="relative w-full">
      {currency && (
        <span
          className={classNames(
            'absolute left-0 z-[1] text-[#616161]',
            {
              'bottom-[13px]': !classNameCurrency?.includes('bottom-') && !error,
              'bottom-[38px]': !classNameCurrency?.includes('bottom-') && !!error,
              'pl-[6px]': withBorder,
            },
            classNameCurrency,
          )}
        >
          {currency}
        </span>
      )}
      <TextInput
        label={label}
        name={name ?? 'money'}
        className={classNames(className)}
        style={{
          paddingLeft,
        }}
        // type="number"
        disabled={disabled}
        min={allowFractional ? step : 1}
        step={allowFractional ? step : 1}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={handleChange}
        withBorder={withBorder}
        error={error}
      />
    </div>
  );
};

export default CurrencyInput;
